<template>
  <div class="lp-page">
    <nav class="landing-nav" :class="{ scrolled: navScrolled }">
      <div class="nav-inner">
        <div class="nav-logo" @click="goHome">
          <div class="logo-mark">Z</div>
          <span class="logo-text">智磐 AI</span>
        </div>

        <div class="nav-actions">
          <a class="nav-link" :class="{ active: isActive('/home/services') }" @click.prevent="goServices">企业服务</a>
          <a class="nav-link" :class="{ active: isActive('/home/usage') }" @click.prevent="goUsage">使用说明</a>
          <button class="btn-primary" @click="goApp">即刻开始</button>
        </div>

        <button class="mobile-toggle" @click="mobileOpen = !mobileOpen">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>
      </div>

      <div v-if="mobileOpen" class="mobile-menu">
        <a class="mobile-link" :class="{ active: isActive('/home/services') }" @click.prevent="goServices(); mobileOpen = false">企业服务</a>
        <a class="mobile-link" :class="{ active: isActive('/home/usage') }" @click.prevent="goUsage(); mobileOpen = false">使用说明</a>
        <button class="btn-primary mobile-cta" @click="goApp">即刻开始</button>
      </div>
    </nav>

    <main class="lp-main">
      <section class="lp-hero">
        <div class="lp-container">
          <span class="lp-badge">产品文档</span>
          <h1>智磐 AI 使用说明</h1>
          <p>
            本页用于指导你从零开始使用智磐 AI 完成跨境电商素材生产。
            按照推荐流程，可在一条链路中完成抠图、套图、裂变、视频与文案生成。
          </p>
          <div class="lp-kpi">
            <div class="lp-kpi-item">
              <span class="lp-kpi-num">5s</span>
              <span class="lp-kpi-label">平均出图速度</span>
            </div>
            <div class="lp-kpi-item">
              <span class="lp-kpi-num">1+6</span>
              <span class="lp-kpi-label">宣传图镜头方案</span>
            </div>
            <div class="lp-kpi-item">
              <span class="lp-kpi-num">10x</span>
              <span class="lp-kpi-label">素材生产效率</span>
            </div>
          </div>
        </div>
      </section>

      <section class="lp-section">
        <div class="lp-container">
          <h2 class="lp-section-title">快速上手流程（推荐）</h2>
          <p class="lp-section-desc">
            第一次使用建议按以下顺序操作，可避免参数缺失与重复返工。
          </p>
          <div class="lp-steps">
            <article class="lp-step" v-for="step in quickSteps" :key="step.title">
              <span class="lp-step-num">{{ step.no }}</span>
              <div>
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </div>
            </article>
          </div>
          <div class="lp-highlight">
            提示：若提示“未登录”或“算力不足”，请先登录账号并确认余额；若接口报错，请到“API 配置”检查 Key 与模型是否可用。
          </div>
        </div>
      </section>

      <section class="lp-section">
        <div class="lp-container">
          <h2 class="lp-section-title">模块功能说明</h2>
          <p class="lp-section-desc">
            每个模块可单独使用，也可以串联成完整素材生产流水线。
          </p>
          <div class="lp-grid-3">
            <article class="lp-card" v-for="item in modules" :key="item.title">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="lp-section">
        <div class="lp-container">
          <h2 class="lp-section-title">常见问题</h2>
          <p class="lp-section-desc">上线初期最常见的三类问题和建议处理方式。</p>
          <div class="lp-grid-2">
            <article class="lp-card" v-for="faq in faqs" :key="faq.q">
              <h3>{{ faq.q }}</h3>
              <p>{{ faq.a }}</p>
            </article>
          </div>
        </div>
      </section>
    </main>

    <footer class="lp-footer">
      <div class="lp-container lp-footer-inner">
        <a class="lp-logo" @click.prevent="goHome">
          <span class="lp-logo-mark">Z</span>
          <span class="lp-logo-text">智磐 AI</span>
        </a>
        <p class="lp-footer-copy">&copy; {{ currentYear }} 智磐 AI. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import "@/styles/landing-pages.css"

const router = useRouter()
const route = useRoute()
const currentYear = new Date().getFullYear()
const navScrolled = ref(false)
const mobileOpen = ref(false)

const onScroll = () => {
  navScrolled.value = window.scrollY > 20
}

onMounted(() => window.addEventListener("scroll", onScroll))
onUnmounted(() => window.removeEventListener("scroll", onScroll))

const goHome = () => {
  router.push("/home")
}

const goServices = () => {
  router.push("/home/services")
}

const goUsage = () => {
  router.push("/home/usage")
}

const goApp = () => {
  router.push("/remove")
}

const isActive = (path) => route.path === path

const quickSteps = [
  {
    no: "01",
    title: "登录账号并配置 API",
    desc: "进入工作台后先完成登录，在 API 配置中填写图片、视频、分析模型所需 Key。"
  },
  {
    no: "02",
    title: "抠图并确定主视觉",
    desc: "在抠图工作台上传商品图，建议先产出高质量白底图，作为后续宣传图的参考底图。"
  },
  {
    no: "03",
    title: "批量生成宣传图与裂变图",
    desc: "在宣传图模块做 1+6 方案生成，再将选中图导入裂变模块，快速扩展颜色或型号素材。"
  },
  {
    no: "04",
    title: "生成视频与 Amazon 文案",
    desc: "选择宣传图生成短视频，并在文案模块产出 Listing 标题、卖点与关键词，再用雷达分析热度。"
  }
]

const modules = [
  {
    title: "抠图工作台",
    desc: "支持本地抠图与豆包抠图，适合快速得到白底主图或统一风格场景图。"
  },
  {
    title: "宣传图生成",
    desc: "可自动规划 1+6 镜头提示词，批量生成电商主图、细节图、场景图。"
  },
  {
    title: "裂变图生成",
    desc: "以宣传图为背景底图，替换不同商品图，实现系列化素材快速扩展。"
  },
  {
    title: "宣传视频生成",
    desc: "将宣传图转成 5-15 秒视频，支持任务轮询和结果下载。"
  },
  {
    title: "Amazon 文案",
    desc: "基于图像内容自动撰写 Listing 文案，包含标题、卖点与关键词建议。"
  },
  {
    title: "流量雷达 + 尺码转换",
    desc: "关键词热度分析辅助选词；尺码图可转换为美标并叠加展示底图。"
  }
]

const faqs = [
  {
    q: "为什么点击生成后提示需要登录？",
    a: "工作台功能均为受保护操作，需登录后才能调用接口并进行算力计费。"
  },
  {
    q: "提示接口失败或模型不可用怎么办？",
    a: "请先检查 API Key 是否有效、模型是否配置正确，并确认后端服务已正常启动。"
  },
  {
    q: "如何保证生成结果稳定？",
    a: "建议先固定商品主图与比例，再逐步微调提示词；批量任务尽量使用同一组参考图。"
  },
  {
    q: "如何降低算力消耗？",
    a: "先进行小尺寸试跑确认风格，再批量生成正式素材，可明显减少无效调用。"
  }
]
</script>
